/**
 * Misconception → repair mapping and confusion graph (the "moat", PRD §2.3). Every
 * wrong answer maps to a structured misconception tag and a deterministic repair path.
 * Pure lookups; no I/O.
 */

import type { MisconceptionTag } from './models.js';

/**
 * Repair action per misconception. Several tags can route to one repair path. Unknown
 * tags fall back to a generic `repair_${tag}` so nothing is silently dropped.
 */
const REPAIR: Record<string, string> = {
  'ignores-large-N': 'repair_constraint_to_complexity',
  'ignores-large-Q': 'repair_static_query_preprocessing',
  'value-range-as-index': 'repair_coordinate_compression',
  'misses-coordinate-compression': 'repair_coordinate_compression',
  'misses-memory-limit': 'repair_memory_budget',
  'forces-graph-model': 'repair_model_selection',
  'misses-state-graph': 'repair_state_graph_modeling',
  'treats-tree-as-general-graph': 'repair_tree_vs_graph',
  'misses-interval-events': 'repair_interval_sweep',
  'uses-BFS-on-weighted-graph': 'repair_shortest_path_selection',
  'uses-Dijkstra-on-unweighted-graph-unnecessarily': 'repair_shortest_path_selection',
  'chooses-DP-state-too-large': 'repair_dp_state_design',
  'uses-greedy-without-monotonicity': 'repair_greedy_exchange_argument',
  'misses-offline-query': 'repair_offline_queries',
  'off-by-one-prefix-range': 'repair_prefix_indexing',
  'integer-overflow': 'repair_overflow_awareness',
  'comparator-not-strict': 'repair_strict_comparator',
  'invariant-too-weak': 'repair_invariant_strength',
  'assumes-positive-values-for-sliding-window': 'repair_sliding_window_monotonicity',
};

export function repairFor(tag: MisconceptionTag): string {
  return REPAIR[tag] ?? `repair_${tag}`;
}

/**
 * Concept pairs students confuse (PRD F-05). Used to schedule discriminator drills.
 * Symmetric pairs; lookup returns the partner concept(s).
 */
const CONFUSION_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['prefix_sum', 'fenwick'],
  ['sliding_window', 'prefix_hashmap'],
  ['bfs', 'dijkstra'],
  ['dp', 'greedy'],
  ['tree_path', 'general_graph_shortest_path'],
];

export function confusedWith(concept: string): readonly string[] {
  const out: string[] = [];
  for (const [a, b] of CONFUSION_PAIRS) {
    if (a === concept) out.push(b);
    else if (b === concept) out.push(a);
  }
  return out;
}

/** Recall card text per misconception (PRD F-14). Falls back to a generic prompt. */
const REVIEW_TEXT: Record<string, { prompt: string; answer: string }> = {
  'ignores-large-Q': {
    prompt:
      'Many queries over an unchanging array. What do you precompute, and why not brute force?',
    answer: 'Prefix sums: O(N) build, O(1) query. Per-query scanning is O(NQ) and times out.',
  },
  'value-range-as-index': {
    prompt: 'Values up to 1e9 but only N of them matter. How do you index them?',
    answer: 'Coordinate compression — map the N distinct values to 0..N-1.',
  },
  'misses-coordinate-compression': {
    prompt: 'When does a huge value range NOT need a huge array?',
    answer: 'When only N distinct values appear — compress coordinates first.',
  },
  'wrong-priority-queue-order': {
    prompt: 'BFS vs Dijkstra: which order do you pop, and when?',
    answer: 'BFS pops FIFO (unweighted); Dijkstra pops the smallest tentative distance (weighted).',
  },
  'off-by-one-prefix-range': {
    prompt: 'Range sum [l, r] from a 1-based prefix array?',
    answer: 'prefix[r] − prefix[l−1]. Subtracting prefix[l] drops a[l].',
  },
  'uses-greedy-without-monotonicity': {
    prompt: 'When does “take the largest that fits” greedy fail?',
    answer: 'When the coin/weight system is not canonical — e.g. coins {1,3,4}, amount 6.',
  },
  'invariant-too-weak': {
    prompt: "What makes Dijkstra's popped-node distance final?",
    answer: 'Non-negative weights: once popped, no later path through the frontier is shorter.',
  },
};

export function reviewCardText(tag: MisconceptionTag): { prompt: string; answer: string } {
  return (
    REVIEW_TEXT[tag] ?? {
      prompt: `Recall the fix for: ${tag.replace(/-/g, ' ')}.`,
      answer: `Apply ${repairFor(tag).replace(/_/g, ' ')}.`,
    }
  );
}
